import { LoginDialogComponent } from '../login-dialog/login-dialog.component';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { RootReducer, Store } from '@app/app.reducers';
import { DialogBase } from '@app/dialog/dialog-base';
import { DialogService } from '@app/dialog/services/dialog.service';
import { popErrorToast } from '@app/shared/reducers/toast.reducer';
import { CognitoService } from '@app/shared/services/cognito.service';
import { hasProp } from '@app/shared/utils/util';
import {
	getRequestingToken,
	getRequestingTokenSuccess,
	reset,
	setUsername,
} from '@app/user/reducers/registration.reducer';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

@Component({
	selector: 'g-password-request-dialog',
	templateUrl: './password-request-dialog.component.html',
	styleUrls: ['./password-request-dialog.component.scss'],
	standalone: false,
})
export class PasswordRequestDialogComponent extends DialogBase implements OnInit, OnDestroy {
	protected store: Store<RootReducer.State>;
	private readonly fb = inject(UntypedFormBuilder);
	private readonly cognito = inject(CognitoService);
	private readonly dialogService = inject(DialogService);
	router = inject(Router);

	form: UntypedFormGroup;
	isLoginLinkHidden: boolean;

	isRequestingToken$: Observable<boolean>;
	isRequestingTokenSuccess$: Observable<boolean>;

	private usernameSubscription?: Subscription;

	formErrors = {
		username: '',
	};

	validationMessages: Record<string, Record<string, string>> = {
		username: {
			required: $localize`:@@PasswordRequestEmailRequired:Email is required.`,
			email: $localize`:@@PasswordRequestEmailValidity:Must be a valid email.`,
		},
	};

	constructor() {
		const store = inject<Store<RootReducer.State>>(Store);

		super(store);

		this.store = store;
	}

	async onSubmit(): Promise<void> {
		try {
			await this.cognito.requestPasswordReset(this.form.value.username.toLowerCase());
			this.closeDialog();
			await this.router.navigate(['/reset-password']);
		} catch (e) {
			this.store.dispatch(popErrorToast({ title: e.message }));
		}
	}

	setUsername(username: string): void {
		this.store.dispatch(setUsername(username));
	}

	backToLogin(): void {
		this.dialogService.open(LoginDialogComponent);
	}

	createForm(): void {
		this.form = this.fb.group({
			username: ['', [Validators.required, Validators.email]],
		});
		this.usernameSubscription = this.form
			.get('username')
			?.valueChanges.pipe(distinctUntilChanged())
			.subscribe((username) => this.setUsername(username));
		this.form.valueChanges.subscribe(() => this.onValueChanged());
		this.onValueChanged();
	}

	ngOnInit(): void {
		this.createForm();
		this.isRequestingToken$ = this.store.select(getRequestingToken);
		this.isRequestingTokenSuccess$ = this.store.select(getRequestingTokenSuccess);

		this.dialogData$.subscribe(
			(data) =>
				(this.isLoginLinkHidden = (hasProp(data, 'hideLoginLink') && !!data.hideLoginLink) ?? false),
		);
	}

	ngOnDestroy(): void {
		this.usernameSubscription?.unsubscribe();
		this.store.dispatch(reset());
	}

	onValueChanged(): void {
		for (const field of Object.keys(this.formErrors)) {
			this.formErrors[field as keyof typeof this.formErrors] = '';
			const control = this.form.get(field);
			if (control && control.dirty && !control.valid && control.errors) {
				const messages = this.validationMessages[field];
				for (const key of Object.keys(control.errors)) {
					this.formErrors[field as keyof typeof this.formErrors] += `${messages[key]} `;
				}
			}
		}
	}
}
